import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface DisparoRiskAcknowledgmentProps {
  isOpen: boolean;
  workspaceId: string;
  onAccepted: () => void;
}

export function DisparoRiskAcknowledgment({
  isOpen,
  workspaceId,
  onAccepted,
}: DisparoRiskAcknowledgmentProps) {
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleAccept = async () => {
    if (!checked) {
      toast({
        title: "Confirme o aviso para continuar",
        description: "Você precisa marcar a caixa de confirmação antes de prosseguir.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.rpc("accept_disparos_risk", {
        p_workspace_id: workspaceId,
      });

      if (error) {
        console.error("Error saving disparos risk acknowledgment:", error);
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar sua confirmação. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      onAccepted();
    } catch (err) {
      console.error("Unexpected error:", err);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Atenção: uso de número dedicado</AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-3">
            <p>
              Recomendamos fortemente <strong>não</strong> usar o número oficial de WhatsApp da empresa para
              disparos em massa.
            </p>
            <p className="text-sm">
              Utilize um chip ou número dedicado para essa finalidade, a fim de evitar o banimento do número
              oficial da empresa.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="disparos-risk-ack"
              checked={checked}
              onCheckedChange={(value) => setChecked(value === true)}
              disabled={isLoading}
            />
            <Label htmlFor="disparos-risk-ack" className="text-sm leading-relaxed cursor-pointer">
              Li e entendo os riscos. Confirmo que usarei um número dedicado para disparos em massa.
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <Button onClick={handleAccept} disabled={!checked || isLoading} className="w-full">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              "Aceitar e Continuar"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
